// @ts-check

/**
 * @file Provides the shared confirmation modal bridge.
 */

/**
 * @typedef {object} ConfirmModalOptions
 * @property {string} [confirmLabel] Label shown on the confirm action.
 * @property {string} [cancelLabel] Label shown on the cancel action.
 * @property {string} [title] Optional modal title.
 * @property {"default" | "destructive"} [tone] Visual tone for the confirm action.
 */

/**
 * @typedef {object} ConfirmModalState
 * @property {string} message Message shown in the confirmation body.
 * @property {ConfirmModalOptions} options Rendering options for labels and tone.
 * @property {(ok: boolean) => void} resolve Callback that resolves the pending confirmation.
 */

/** @type {((state: ConfirmModalState | null) => void) | null} */
let setHostState = null

/**
 * Registers the host state setter used to render confirmation state.
 * @param {((state: ConfirmModalState | null) => void) | null} setter Function called with modal state updates.
 * @returns {void} Nothing.
 */
export function registerConfirmModalHost(setter) {
  setHostState = setter
}

/**
 * Show a centered confirm modal and resolve `true` if the user confirms, `false` otherwise.
 * @param {string} message Confirmation message to show.
 * @param {ConfirmModalOptions} [options] Optional labels, title, and tone.
 * @returns {Promise<boolean>} Whether the user confirmed the action.
 */
export default function confirmModal(message, options = {}) {
  return new Promise((resolve) => {
    if (!setHostState) {
      if (typeof window !== "undefined" && typeof window.confirm === "function") {
        resolve(window.confirm(message))
      } else {
        resolve(false)
      }

      return
    }

    setHostState({
      message,
      options,
      resolve: (ok) => {
        setHostState && setHostState(null)
        resolve(ok)
      }
    })
  })
}
